import { create } from 'zustand';
import { errorHandler } from '@/utils/functions/errorHandler';
import { getKpiApprovals } from '@/api/myKpi';

export interface IKpiApproval {
  id: number;
  tenant_id: number;
  template_name: string;
  period: string;
  start_date: string;
  end_date: string;
  total_kpi: number;
  total_weight: number;
  status: string;
  progress: number | null;
  score: number | null;
  user_id: number;
  employee_name: string;
  employee_role: string;
  created_at: string;
  updated_at: string;
}

interface PaginationParams {
  page?: number;
  limit?: number;
  page_size?: number;
  order?: string;
  order_by?: string;
  search?: string;
  period?: string;
  status?: string;
}

interface PaginatedData {
  content: IKpiApproval[];
  totalElements: number;
  totalPages: number;
}

interface KpiApprovalState {
  kpiApprovals: IKpiApproval[];
  paginatedData: PaginatedData | null;
  isLoading: boolean;
  error: unknown;

  getKpiApprovals: (tenantId: string | number, params?: PaginationParams) => Promise<void>;
  clearKpiApprovals: () => void;
}

const useKpiApprovalState = create<KpiApprovalState>(set => ({
  kpiApprovals: [],
  paginatedData: null,
  isLoading: false,
  error: null,

  clearKpiApprovals: () => set({ kpiApprovals: [], paginatedData: null }),

  getKpiApprovals: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getKpiApprovals(tenantId, params as Record<string, unknown>);
      const raw = response; // getKpiApprovals directly returns the response body
      const content: IKpiApproval[] = raw?.data?.content ?? raw?.data ?? raw?.content ?? [];
      const total: number = raw?.data?.totalElements ?? raw?.totalElements ?? raw?.total ?? content.length;
      const limit = raw?.data?.size ?? raw?.limit ?? params?.limit ?? params?.page_size ?? 10;
      const totalPages: number = raw?.data?.totalPages ?? raw?.totalPages ?? (Math.ceil(total / limit) || 1);

      set({
        kpiApprovals: content,
        paginatedData: {
          content,
          totalElements: total,
          totalPages,
        },
      });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },
}));

export default useKpiApprovalState;
